import React from 'react';
import { Droplet, Sun, Sliders, Minimize, Circle, MoveUp, Square, Waves, CloudFog } from 'lucide-react';

interface ImageControlsProps {
  brightness: number;
  contrast: number;
  saturation: number;
  hue: number;
  shadowType: 'none' | 'medium' | 'hard' | 'floating' | 'contact' | 'reflection';
  autoCrop: boolean;
  sharpness: number;
  paddingAmount: number;
  onBrightnessChange: (value: number) => void;
  onContrastChange: (value: number) => void;
  onSaturationChange: (value: number) => void;
  onHueChange: (value: number) => void;
  onShadowTypeChange: (type: 'none' | 'medium' | 'hard' | 'floating' | 'contact' | 'reflection') => void;
  onCenterProduct: () => void;
  onAutoCropChange: (enabled: boolean) => void;
  onSharpnessChange: (value: number) => void;
  onPaddingAmountChange: (amount: number) => void;
}

export const ImageControls: React.FC<ImageControlsProps> = ({
  brightness,
  contrast,
  saturation,
  hue,
  shadowType,
  autoCrop,
  sharpness,
  paddingAmount,
  onBrightnessChange,
  onContrastChange,
  onSaturationChange,
  onHueChange,
  onShadowTypeChange,
  onCenterProduct,
  onAutoCropChange,
  onSharpnessChange,
  onPaddingAmountChange,
}) => {
  return (
    <div className="w-full">
      <div className="space-y-5">
        <div className="pb-5 mb-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div>
                <p className="text-sm font-medium text-gray-900">Intelligent Crop</p>
                <p className="text-xs text-gray-500 mt-0.5">For consistent sizing</p>
              </div>
            </div>
            <button
              onClick={() => onAutoCropChange(!autoCrop)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoCrop ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoCrop ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {autoCrop && (
            <div className="mt-3">
              <p className="text-xs font-medium text-gray-600 mb-2">Select white space around product</p>
              <div className="flex gap-1">
                {[5, 10, 20, 30, 50].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => onPaddingAmountChange(amount)}
                    className={`flex-1 py-1.5 px-2 rounded text-xs transition-all duration-200 ${
                      paddingAmount === amount
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {amount}%
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Shadow */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">Shadow</p>
          <p className="text-xs text-gray-500 mb-2.5">Add depth beneath your product</p>
          <div className="grid grid-cols-3 gap-1.5">
            {([
              { type: 'none' as const, label: 'None', icon: Circle },
              { type: 'medium' as const, label: 'Medium', icon: CloudFog },
              { type: 'hard' as const, label: 'Hard', icon: Square },
              { type: 'floating' as const, label: 'Float', icon: MoveUp },
              { type: 'contact' as const, label: 'Contact', icon: Minimize },
              { type: 'reflection' as const, label: 'Reflect', icon: Waves },
            ]).map(({ type, label, icon: Icon }) => (
              <button
                key={type}
                onClick={() => onShadowTypeChange(type)}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                  shadowType === type
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Brightness */}
        <div>
          <label className="flex items-center justify-between text-sm text-gray-700 mb-1">
            <div className="flex items-center gap-2">
              <Sun className="w-4 h-4" />
              <span>Brightness</span>
            </div>
            <span className="text-gray-500">{brightness}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="200"
            value={brightness}
            onChange={(e) => onBrightnessChange(Number(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
          />
        </div>
        
        {/* Contrast */}
        <div>
          <label className="flex items-center justify-between text-sm text-gray-700 mb-1">
            <div className="flex items-center gap-2">
              <Sliders className="w-4 h-4" />
              <span>Contrast</span>
            </div>
            <span className="text-gray-500">{contrast}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="200"
            value={contrast}
            onChange={(e) => onContrastChange(Number(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
          />
        </div>

        {/* Saturation */}
        <div>
          <label className="flex items-center justify-between text-sm text-gray-700 mb-1">
            <div className="flex items-center gap-2">
              <Droplet className="w-4 h-4" />
              <span>Saturation</span>
            </div>
            <span className="text-gray-500">{saturation}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="200"
            value={saturation}
            onChange={(e) => onSaturationChange(Number(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
          />
        </div>

        {/* Hue */}
        <div>
          <label className="flex items-center justify-between text-sm text-gray-700 mb-1">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-gray-700">🎨</span>
              <span>Hue</span>
            </div>
            <span className="text-gray-500">{hue}°</span>
          </label>
          <input
            type="range"
            min="0"
            max="360"
            value={hue}
            onChange={(e) => onHueChange(Number(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
          />
        </div>

        {/* Sharpness */}
        <div>
          <label className="flex items-center justify-between text-sm text-gray-700 mb-1">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-gray-700">*</span>
              <span>Sharpness</span>
            </div>
            <span className="text-gray-500">{sharpness}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={sharpness}
            onChange={(e) => onSharpnessChange(Number(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
          />
        </div>
      </div>
    </div>
  );
};